import { Badge, Card, Code, Group, Stack, Text } from "@mantine/core";
import { useMemo } from "react";

import { type Attempt } from "../storage";

export interface AttemptBoardProps {
  readonly attempts: readonly Attempt[];
}

/**
 * Renders the player's stored top scores in descending order of
 * canonical blend. Storage already enforces this invariant, but we
 * re-sort at the render boundary as defense — that way the board
 * stays deterministic if it's ever handed unsorted props (e.g. via a
 * test fixture or a future code path that forgets the contract).
 */
export function AttemptBoard({ attempts }: AttemptBoardProps) {
  const ranked = useMemo(() => sortByBlendDesc(attempts), [attempts]);

  if (ranked.length === 0) {
    return (
      <Card withBorder padding="md" aria-label="top scores empty">
        <Text c="dimmed" size="sm">
          no scores yet — submit a sentence to land your first.
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap="xs" role="list" aria-label="top scores">
      {ranked.map((attempt, i) => (
        <AttemptSlot key={`${i.toString()}-${attempt.sentence}`} attempt={attempt} rank={i + 1} />
      ))}
    </Stack>
  );
}

function sortByBlendDesc(attempts: readonly Attempt[]): readonly Attempt[] {
  return [...attempts].sort((a, b) => {
    if (a.canonicalBlend === b.canonicalBlend) return 0;
    return a.canonicalBlend > b.canonicalBlend ? -1 : 1;
  });
}

interface AttemptSlotProps {
  readonly attempt: Attempt;
  readonly rank: number;
}

function AttemptSlot({ attempt, rank }: AttemptSlotProps) {
  return (
    <Card withBorder role="listitem" aria-label={`rank ${rank.toString()}`} py="xs" px="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2}>
          <Text size="sm">{attempt.sentence}</Text>
          <Text size="xs" c="dimmed">
            blend <Code>{attempt.canonicalBlend.toString()}</Code>
          </Text>
        </Stack>
        <Badge variant="light" color={rank === 1 ? "green" : "gray"} aria-label={`rank ${rank.toString()}`}>
          #{rank}
        </Badge>
      </Group>
    </Card>
  );
}
